import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where, addDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Box, Typography, CircularProgress, Button, Divider, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const gold = '#C9A84C';
const dark = '#0D1E35';

const googleProvider = new GoogleAuthProvider();
const appleProvider  = new OAuthProvider('apple.com');

function formatTime(t) {
  if (!t) return '';
  const [h, min] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(min).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatSessionLabel(t) {
  const map = {
    'junior-practice':   'Junior Practice',
    'junior-private':    'Junior Private',
    'junior-drop-in':    'Junior Drop-in',
    'junior-evaluation': 'Junior Evaluation',
    'adult-practice':    'Adult Practice',
    'league':            'League',
    'block-time':        'Block Time',
  };
  return map[t] || t;
}

export default function QuickCheckIn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classId    = searchParams.get('classId');
  const sessionType = searchParams.get('sessionType');

  const [user, setUser]         = useState(undefined); // undefined = loading
  const [classData, setClassData] = useState(null);
  const [athlete, setAthlete]   = useState(null);
  const [plan, setPlan]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [error, setError]       = useState('');

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u || null));
    return unsub;
  }, []);

  // Load class info
  useEffect(() => {
    if (!classId) return;
    getDoc(doc(db, 'classes', classId))
      .then(snap => { if (snap.exists()) setClassData({ id: snap.id, ...snap.data() }); })
      .catch(() => {});
  }, [classId]);

  // Load athlete + plan once signed in
  useEffect(() => {
    if (!user || !sessionType) { if (user !== undefined) setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const isAdult = sessionType.startsWith('adult');
        // Find athlete record
        let athDoc = null;
        const byEmail = await getDocs(query(collection(db, 'athletes'), where('email', '==', user.email)));
        athDoc = byEmail.docs.find(d => d.data().athleteType?.toLowerCase() === (isAdult ? 'adult' : 'junior'));
        if (!athDoc) {
          const byUserId = await getDocs(query(collection(db, 'athletes'), where('userId', '==', user.uid)));
          athDoc = byUserId.docs[0];
        }
        if (athDoc) setAthlete({ id: athDoc.id, ...athDoc.data() });

        // Check if already checked in
        if (classId && athDoc) {
          const attSnap = await getDocs(query(collection(db, 'attendance'),
            where('classId', '==', classId),
            where('athleteId', '==', athDoc.id)
          ));
          if (!attSnap.empty) setAlreadyCheckedIn(true);
        }

        // Find matching plan — use class-specified plan if set, otherwise auto-match
        const planSnap = await getDocs(collection(db, 'plans'));
        const plans = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Re-fetch classData to get qrPlanId (may not be loaded yet when user signs in)
        let qrPlanId = null;
        if (classId) {
          try {
            const cdSnap = await getDoc(doc(db, 'classes', classId));
            if (cdSnap.exists()) qrPlanId = cdSnap.data().qrPlanId || null;
          } catch {}
        }

        let match = null;
        if (qrPlanId) {
          match = plans.find(p => p.id === qrPlanId && p.active);
        }
        if (!match) {
          const isAdult = sessionType.startsWith('adult');
          match = plans.find(p =>
            p.active &&
            p.sessionsIncluded === 1 &&
            (p.athleteType === (isAdult ? 'adult' : 'junior') || p.athleteType === 'both')
          );
        }
        if (match) setPlan(match);
      } catch (e) {
        setError('Failed to load. Please try again.');
      }
      setLoading(false);
    };
    load();
  }, [user, sessionType, classId]);

  const handleSignIn = async (provider) => {
    try { await signInWithPopup(auth, provider); }
    catch (e) { setError('Sign-in failed. Please try again.'); }
  };

  const handleCheckout = async () => {
    if (!plan || !athlete) return;
    setCheckingOut(true);
    setError('');
    try {
      const functions = getFunctions();
      const createSession = httpsCallable(functions, 'createCheckoutSession');
      const successUrl = `${window.location.origin}/payment-success?planId=${plan.id}&athleteId=${athlete.id}&sessions=${plan.sessionsIncluded}&amount=${plan.price}&planName=${encodeURIComponent(plan.planName)}&session_id={CHECKOUT_SESSION_ID}&autoCheckinClassId=${classId}`;
      const cancelUrl = window.location.href;
      const result = await createSession({ planId: plan.id, athleteId: athlete.id, successUrl, cancelUrl });
      window.location.href = result.data.url;
    } catch (e) {
      setError('Checkout failed. Please try again.');
      setCheckingOut(false);
    }
  };

  const Wrap = ({ children }) => (
    <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
      <Box sx={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '16px', p: { xs: 3, sm: 5 }, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <img src="/torch_logo.png" alt="Torch" style={{ width: 50, marginBottom: 12 }} />
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.6rem', letterSpacing: '3px', color: 'white', mb: 0.5 }}>
          TORCH BEACH VB
        </Typography>
        {children}
      </Box>
    </Box>
  );

  // Loading auth state
  if (user === undefined || (user && loading)) return (
    <Wrap><CircularProgress sx={{ color: gold, mt: 3 }} /></Wrap>
  );

  // Class info card
  const ClassCard = () => classData ? (
    <Box sx={{ mt: 2, mb: 3, p: 2, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', textAlign: 'left' }}>
      <Typography sx={{ color: gold, fontWeight: 700, fontSize: '0.95rem' }}>{formatSessionLabel(classData.sessionType)}{classData.skillGroup ? ` — ${classData.skillGroup}` : ''}</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', mt: 0.5 }}>
        {classData.date} · {formatTime(classData.startTime)}–{formatTime(classData.endTime)}
        {classData.courtNumber ? ` · Court ${classData.courtNumber.replace('court-', '')}` : ''}
      </Typography>
    </Box>
  ) : null;

  // Already checked in
  if (alreadyCheckedIn) return (
    <Wrap>
      <ClassCard />
      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: gold, mt: 1, mb: 1 }} />
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>Already Checked In!</Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mt: 1 }}>You're on the list for this class.</Typography>
    </Wrap>
  );

  // Not signed in
  if (!user) return (
    <Wrap>
      <ClassCard />
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', mb: 3, mt: 1 }}>
        Sign in to purchase a session and check in.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Button fullWidth variant="contained" onClick={() => handleSignIn(googleProvider)}
        sx={{ mb: 1.5, backgroundColor: 'white', color: '#333', fontWeight: 700, '&:hover': { backgroundColor: '#f0f0f0' } }}>
        <img src="https://developers.google.com/identity/images/g-logo.png" alt="" style={{ width: 18, marginRight: 8 }} />
        Sign in with Google
      </Button>
      <Button fullWidth variant="contained" onClick={() => handleSignIn(appleProvider)}
        sx={{ backgroundColor: 'black', color: 'white', fontWeight: 700, '&:hover': { backgroundColor: '#222' } }}>
        🍎 Sign in with Apple
      </Button>
    </Wrap>
  );

  // Signed in but no plan found
  if (!plan) return (
    <Wrap>
      <ClassCard />
      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mt: 2 }}>
        No single-session plan found for this class type. Please purchase a plan from the main store.
      </Typography>
      <Button variant="outlined" onClick={() => navigate('/training-plans')} sx={{ mt: 2, borderColor: gold, color: gold }}>
        Go to Training Plans
      </Button>
    </Wrap>
  );

  // Signed in + plan found — show purchase button
  return (
    <Wrap>
      <ClassCard />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', mb: 1 }}>
        Hi {athlete?.firstName || user.displayName?.split(' ')[0] || 'there'}! Purchase a session to check in.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2.5rem', color: gold, lineHeight: 1 }}>
          ${plan.price}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>{plan.planName}</Typography>
      </Box>
      <Button fullWidth variant="contained" onClick={handleCheckout} disabled={checkingOut}
        sx={{ background: gold, color: dark, fontWeight: 700, fontSize: '1rem', py: 1.5, borderRadius: '10px',
          '&:hover': { background: '#e0be6a' }, '&:disabled': { background: 'rgba(201,168,76,0.4)' } }}>
        {checkingOut ? <CircularProgress size={22} sx={{ color: dark }} /> : `Buy & Check In — $${plan.price}`}
      </Button>
      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', mt: 1.5 }}>
        Secure checkout via Stripe
      </Typography>
    </Wrap>
  );
}
